/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';
import { type ReactNode, useId, useMemo } from 'react';

import { customLabelAndNoAriaLabelWarningMessage } from '../_common/messages';
import {
  type CommonProps,
  type HtmlAttributes,
  type TooltipObjectProps,
} from '../_common/types';
import { needleWarningMessage } from '../_common/utils';
import { ExclamationCircleIconSolid } from '../icons/generated/heroIcons/ExclamationCircleIconSolid';
import { InformationCircleIconOutline } from '../icons/generated/heroIcons/InformationCircleIconOutline';
import { Tooltip } from '../tooltip';

/**
 *
 *
 * Types
 *
 *
 */

/**
 * @remarks
 * When `moreInformationText` is provided, a tooltip is rendered next to the label, and the `tooltipProps` prop is used to configure the tooltip. The `TooltipObjectProps` type is defined as: `{ root?: Partial<React.ComponentProps<typeof Tooltip>>; trigger?: Partial<React.ComponentProps<typeof Tooltip.Trigger>>; content?: Partial<React.ComponentProps<typeof Tooltip.Content>>; }`, see the Tooltip component for more details.
 */
type TextAreaProps = {
  /** The label displayed above the textarea */
  label?: ReactNode;

  /** Whether the textarea should take the full available width */
  isFluid?: boolean;

  /** Assistive text displayed below the textarea */
  helpText?: string | ReactNode;

  /** Error message displayed below the textarea. When provided, it overrides `helpText` and shows an error icon */
  errorText?: string | ReactNode;

  /** Size of the textarea */
  size?: 'small' | 'medium' | 'large';

  // TODO v5: use isRequired and showRequiredOrOptionalLabel like in TextInput
  /** Whether to display the Optional label next to the field label */
  isOptional?: boolean;

  /** Text displayed in the information tooltip shown next to the label */
  moreInformationText?: React.ReactNode;

  /** Props for the Tooltip component used by the information icon */
  tooltipProps?: TooltipObjectProps;

  /** Whether the textarea is disabled */
  isDisabled?: boolean;

  /** Additional HTML attributes passed to the textarea element */
  htmlAttributes?: HtmlAttributes<'textarea'>;

  /** Aria label for accessibility when no visible label is provided */
  ariaLabel?: string;

  /** Additional class names */
  className?: string;

  /** Additional styles applied to the root element */
  style?: React.CSSProperties;

  /** Whether the textarea is read-only */
  isReadOnly?: boolean;

  /** The current value of the textarea */
  value?: React.ComponentPropsWithoutRef<'textarea'>['value'];

  /** Placeholder text displayed when the textarea is empty */
  placeholder?: React.ComponentPropsWithoutRef<'textarea'>['placeholder'];
};

/**
 *
 *
 * TextArea Component
 *
 *
 */

export const TextArea = ({
  label,
  isFluid = false,
  errorText,
  helpText,
  isOptional = false,
  moreInformationText,
  size = 'medium',
  ariaLabel,
  tooltipProps,
  isDisabled = false,
  className,
  isReadOnly = false,
  style,
  value,
  placeholder,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'textarea', TextAreaProps>) => {
  const helpTextId = useId();
  const errorTextId = useId();
  const containerWrappingClasses = classNames(
    `ndl-text-area ndl-type-text`,
    className,
    {
      'ndl-disabled': isDisabled,
      'ndl-has-error': errorText,
      'ndl-has-icon': errorText,
      'ndl-has-trailing-icon': errorText,
      'ndl-large': size === 'large',
      'ndl-medium': size === 'medium',
      'ndl-small': size === 'small',
    },
  );

  const hasEmptyLabelValue = !label || label === '';

  const labelWrappingClasses = classNames('ndl-text-area-label', {
    'ndl-fluid': isFluid,
    'ndl-text-area-no-label': hasEmptyLabelValue,
  });

  const isCustomLabel = label && typeof label !== 'string';
  const hasCustomLabelAndNoAriaLabel = isCustomLabel && !ariaLabel;

  useMemo(() => {
    if (!label && !ariaLabel) {
      needleWarningMessage(
        'A TextArea without a label does not have an aria label, be sure to include an aria label for screen readers link: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI',
      );
    }

    if (hasCustomLabelAndNoAriaLabel) {
      needleWarningMessage(customLabelAndNoAriaLabelWarningMessage);
    }
  }, [label, ariaLabel, hasCustomLabelAndNoAriaLabel]);

  const informationIconClasses = classNames({
    'ndl-information-icon-large': size === 'large',
    'ndl-information-icon-small': size === 'small' || size === 'medium',
  });

  const combinedAriaDescribedby = useMemo(() => {
    const ariaDescribedby = [];
    if (helpText && !errorText) {
      ariaDescribedby.push(helpTextId);
    } else if (errorText) {
      ariaDescribedby.push(errorTextId);
    }
    return ariaDescribedby.join(' ');
  }, [helpText, errorText, helpTextId, errorTextId]);

  return (
    <div className={containerWrappingClasses}>
      {/* We enable implicit label wrapping */}
      {/* Source: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI */}
      <label className={labelWrappingClasses}>
        <div className="ndl-text-area-wrapper">
          <textarea
            disabled={isDisabled}
            ref={ref}
            aria-label={ariaLabel}
            aria-invalid={Boolean(errorText)}
            readOnly={isReadOnly}
            value={value}
            placeholder={placeholder}
            style={style}
            aria-describedby={combinedAriaDescribedby}
            {...restProps}
            {...htmlAttributes}
          />
        </div>
        {!hasEmptyLabelValue && (
          <div className="ndl-text-area-wrapper">
            <span className="ndl-text-area-label-text">{label}</span>
            {Boolean(moreInformationText) && (
              <Tooltip type="simple" {...tooltipProps?.root}>
                <Tooltip.Trigger {...tooltipProps?.trigger}>
                  <div
                    className={informationIconClasses}
                    data-testid="ndl-information-icon"
                  >
                    <InformationCircleIconOutline />
                  </div>
                </Tooltip.Trigger>
                <Tooltip.Content {...tooltipProps?.content}>
                  {moreInformationText}
                </Tooltip.Content>
              </Tooltip>
            )}
            {isOptional && (
              <span className="ndl-text-area-optional">Optional</span>
            )}
          </div>
        )}
      </label>
      {helpText && !errorText && (
        <div className="ndl-text-area-msg" aria-live="polite" id={helpTextId}>
          {helpText}
        </div>
      )}
      {errorText && (
        <div className="ndl-text-area-msg">
          <div className="ndl-error-icon">
            <ExclamationCircleIconSolid />
          </div>
          <div className="ndl-error-text" id={errorTextId} aria-live="polite">
            {errorText}
          </div>
        </div>
      )}
    </div>
  );
};
